import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  EmailVerificationIcon,
  LimitedAccessVerificationIcon,
  Modal,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { CardActivationVariants } from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal/VerifyMyCardModal.constants';

import { useGetVerificationEmail } from './hooks';
import {
  LimitedAccessVerificationMethod,
  LimitedAccessVerificationStep,
} from './LimitedAccessVerification.types';
import LimitedAccessVerificationEmail from './LimitedAccessVerificationEmail';
import LimitedAccessVerificationSelectMethod from './LimitedAccessVerificationSelectMethod';

interface Props {
  visible: boolean;
  onSetVisible: (value: boolean) => void;
  onOpenCardVerification: () => void;
  onSuccess: () => void;
  setIsActivationSuccess?: (value: CardActivationVariants) => void;
}
const LimitedAccessVerification: React.FC<Props> = ({
  visible,
  onSetVisible,
  onOpenCardVerification,
  onSuccess,
  setIsActivationSuccess,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const email = useGetVerificationEmail();
  const [currentStep, setCurrentStep] = useState(LimitedAccessVerificationStep.SelectMethod);

  const handleOpenMethod = (method: LimitedAccessVerificationMethod) => {
    if (method === LimitedAccessVerificationMethod.CardCode) {
      onSetVisible(false);
      onOpenCardVerification();
    }
    if (method === LimitedAccessVerificationMethod.Email) {
      setCurrentStep(LimitedAccessVerificationStep.Email);
    }
  };

  const handleShowSuccess = useCallback(() => {
    onSuccess();
    onSetVisible(false);
  }, [onSetVisible, onSuccess]);

  return (
    <>
      <Modal visible={visible} testId="limited-access-verification-modal" onSetVisible={onSetVisible}>
        <Box direction="column">
          {currentStep === LimitedAccessVerificationStep.SelectMethod && (
            <Box
              data-testid="limited-access-verification_select-method-header"
              align="center"
              justify="center"
              margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
            >
              <LimitedAccessVerificationIcon />
              <Text
                data-testid="limited-access-verification_select-method-title"
                textAlign="center"
                size="2xl"
                weight={700}
                margin={{ vertical: 'spacing12' }}
              >
                {t('Choose Your Verification Method')}
              </Text>
              <Text data-testid="limited-access-verification_select-method-description" textAlign="center">
                {t('Please select how you would like to verify your account')}
              </Text>
            </Box>
          )}
          {currentStep === LimitedAccessVerificationStep.Email && (
            <Box
              data-testid="limited-access-verification_email-header"
              align="center"
              justify="center"
              margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
            >
              <EmailVerificationIcon />
              <Text
                data-testid="limited-access-verification_email-title"
                textAlign="center"
                size="2xl"
                weight={700}
                margin={{ vertical: 'spacing12' }}
              >
                {t('Email Verification')}
              </Text>
              <Text data-testid="limited-access-verification_email-description" textAlign="center">
                {t('Please enter the 6-digit verification code sent to ')}
                <Text weight={700}>{email}.</Text>
              </Text>
              <Text textAlign="center">{t('The code is valid for 10 minutes.')}</Text>
            </Box>
          )}
          {currentStep === LimitedAccessVerificationStep.SelectMethod && (
            <LimitedAccessVerificationSelectMethod
              onSetVisible={onSetVisible}
              onOpenMethod={handleOpenMethod}
            />
          )}
          {currentStep === LimitedAccessVerificationStep.Email && (
            <LimitedAccessVerificationEmail
              onSuccess={handleShowSuccess}
              setIsActivationSuccess={setIsActivationSuccess}
              onBack={() => {
                setCurrentStep(LimitedAccessVerificationStep.SelectMethod);
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};
export default React.memo(LimitedAccessVerification);
